/**
 * InputExamples.tsx
 * Comprehensive examples of HeroUI Input component
 * 
 * Showcases all HeroUI Input features using our shared components
 */

import React, { useState } from 'react';
import { PlaygroundPageLayout } from '../shared';
import { Input } from '@enfomentis/ui';
import { Card, CardHeader, CardBody } from '@enfomentis/ui';
import { Badge } from '@enfomentis/ui';
import { Button } from '@enfomentis/ui';
import { 
  Mail, 
  Search, 
  Eye, 
  EyeOff,
  Lock,
  Phone,
  MapPin,
  CreditCard,
  Globe,
  DollarSign,
  Percent
} from 'lucide-react';

export const InputExamples: React.FC = () => {
  const [values, setValues] = useState<Record<string, string>>({
    basic: '',
    bordered: '',
    underlined: '',
    faded: '',
    flat: '',
    email: '',
    password: '',
    search: '',
    number: '',
    url: '',
    tel: '',
    withStartContent: '',
    withEndContent: '',
    withBothContent: '',
    clearable: 'Some initial text',
    required: '',
    disabled: 'Cannot edit this',
    readonly: 'Read-only value',
    validated: '',
    small: '',
    medium: '',
    large: '',
    primary: '',
    secondary: '',
    success: '',
    warning: '',
    danger: ''
  });

  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const handleValueChange = (key: string, value: string) => {
    setValues(prev => ({ ...prev, [key]: value }));
    
    // Clear error when user starts typing
    if (errors[key]) {
      setErrors(prev => ({ ...prev, [key]: '' }));
    }
  };

  const validateEmail = (email: string): string => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email) && email.length > 0) {
      return 'Please enter a valid email address';
    }
    return '';
  };

  const handleEmailValidation = () => {
    const error = validateEmail(values['validated'] || '');
    setErrors(prev => ({ ...prev, validated: error }));
  };

  return (
    <PlaygroundPageLayout>
      <div className="space-y-8">
        
        {/* Header */}
        <div className="space-y-2">
          <h1 className="text-3xl font-bold text-text-primary">Input Examples</h1>
          <p className="text-text-secondary">
            Comprehensive showcase of HeroUI Input component with automatic theme integration
          </p>
        </div>

        {/* Basic Variants */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <h2 className="text-xl font-semibold">Input Variants</h2>
              <Badge color="primary" variant="flat" size="sm">4 variants</Badge>
            </div>
          </CardHeader>
          <CardBody className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Input
                variant="flat"
                label="Flat Variant"
                placeholder="Enter text..."
                value={values['flat'] || ''}
                onValueChange={(value) => handleValueChange('flat', value)}
                description="Flat input with subtle background"
              />
              
              <Input
                variant="bordered"
                label="Bordered Variant"
                placeholder="Enter text..."
                value={values['bordered'] || ''}
                onValueChange={(value) => handleValueChange('bordered', value)}
                description="Input with visible border"
              />
              
              <Input
                variant="underlined"
                label="Underlined Variant"
                placeholder="Enter text..."
                value={values['underlined'] || ''}
                onValueChange={(value) => handleValueChange('underlined', value)}
                description="Input with bottom border only"
              />
              
              <Input
                variant="faded"
                label="Faded Variant"
                placeholder="Enter text..."
                value={values['faded'] || ''}
                onValueChange={(value) => handleValueChange('faded', value)}
                description="Faded appearance input"
              />
            </div>
          </CardBody>
        </Card>

        {/* Input Types */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <h2 className="text-xl font-semibold">Input Types</h2>
              <Badge color="secondary" variant="flat" size="sm">Multiple types</Badge>
            </div>
          </CardHeader>
          <CardBody className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <Input
                type="email"
                variant="bordered"
                label="Email"
                placeholder="you@example.com"
                value={values['email'] || ''}
                onValueChange={(value) => handleValueChange('email', value)}
                startContent={<Mail className="w-4 h-4 text-default-400" />}
              />
              
              <Input
                type="password"
                variant="bordered"
                label="Password"
                placeholder="Enter password"
                value={values['password'] || ''}
                onValueChange={(value) => handleValueChange('password', value)}
                startContent={<Lock className="w-4 h-4 text-default-400" />}
                endContent={
                  <button 
                    className="focus:outline-none" 
                    type="button"
                    onClick={() => setIsPasswordVisible(!isPasswordVisible)}
                  >
                    {isPasswordVisible ? (
                      <EyeOff className="w-4 h-4 text-default-400" />
                    ) : (
                      <Eye className="w-4 h-4 text-default-400" />
                    )}
                  </button>
                }
              />
              
              <Input
                type="search"
                variant="bordered"
                label="Search"
                placeholder="Search..."
                value={values['search'] || ''}
                onValueChange={(value) => handleValueChange('search', value)}
                startContent={<Search className="w-4 h-4 text-default-400" />}
                isClearable
              />
              
              <Input
                type="number"
                variant="bordered"
                label="Number"
                placeholder="0.00"
                value={values['number'] || ''}
                onValueChange={(value) => handleValueChange('number', value)}
                startContent={<DollarSign className="w-4 h-4 text-default-400" />}
              />
              
              <Input
                type="url"
                variant="bordered"
                label="Website"
                placeholder="https://example.com"
                value={values['url'] || ''}
                onValueChange={(value) => handleValueChange('url', value)}
                startContent={<Globe className="w-4 h-4 text-default-400" />}
              />
              
              <Input
                type="tel"
                variant="bordered"
                label="Phone"
                placeholder="+1 (555) 000-0000"
                value={values['tel'] || ''}
                onValueChange={(value) => handleValueChange('tel', value)}
                startContent={<Phone className="w-4 h-4 text-default-400" />}
              />
            </div>
          </CardBody>
        </Card>

        {/* Content Slots */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <h2 className="text-xl font-semibold">Content Slots</h2>
              <Badge color="success" variant="flat" size="sm">Start & End</Badge>
            </div>
          </CardHeader>
          <CardBody className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Input
                variant="bordered"
                label="With Start Content"
                placeholder="Enter location..."
                value={values['withStartContent'] || ''}
                onValueChange={(value) => handleValueChange('withStartContent', value)}
                startContent={<MapPin className="w-4 h-4 text-default-400" />}
              />
              
              <Input
                variant="bordered"
                label="With End Content"
                placeholder="Enter percentage..."
                value={values['withEndContent'] || ''}
                onValueChange={(value) => handleValueChange('withEndContent', value)}
                endContent={<Percent className="w-4 h-4 text-default-400" />}
              />
              
              <Input
                variant="bordered"
                label="Card Number"
                placeholder="1234 5678 9012 3456"
                value={values['withBothContent'] || ''}
                onValueChange={(value) => handleValueChange('withBothContent', value)}
                startContent={<CreditCard className="w-4 h-4 text-default-400" />}
                endContent={
                  <Button size="sm" variant="flat" className="h-6 min-w-unit-12">
                    Verify
                  </Button>
                }
              />
            </div>
          </CardBody>
        </Card>

        {/* Sizes */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <h2 className="text-xl font-semibold">Input Sizes</h2>
              <Badge color="warning" variant="flat" size="sm">3 sizes</Badge>
            </div>
          </CardHeader>
          <CardBody className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <Input
                size="sm"
                variant="bordered"
                label="Small"
                placeholder="Small input"
                value={values['small'] || ''}
                onValueChange={(value) => handleValueChange('small', value)}
              />
              
              <Input
                size="md"
                variant="bordered"
                label="Medium"
                placeholder="Medium input"
                value={values['medium'] || ''}
                onValueChange={(value) => handleValueChange('medium', value)}
              />
              
              <Input
                size="lg"
                variant="bordered"
                label="Large"
                placeholder="Large input"
                value={values['large'] || ''}
                onValueChange={(value) => handleValueChange('large', value)}
              />
            </div>
          </CardBody>
        </Card>

        {/* Colors */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <h2 className="text-xl font-semibold">Input Colors</h2>
              <Badge color="danger" variant="flat" size="sm">Theme colors</Badge>
            </div>
          </CardHeader>
          <CardBody className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <Input
                color="primary"
                variant="bordered"
                label="Primary"
                placeholder="Primary color"
                value={values['primary'] || ''}
                onValueChange={(value) => handleValueChange('primary', value)}
              />
              
              <Input
                color="secondary"
                variant="bordered"
                label="Secondary"
                placeholder="Secondary color"
                value={values['secondary'] || ''}
                onValueChange={(value) => handleValueChange('secondary', value)}
              />
              
              <Input
                color="success"
                variant="bordered"
                label="Success"
                placeholder="Success color"
                value={values['success'] || ''}
                onValueChange={(value) => handleValueChange('success', value)}
              />
              
              <Input
                color="warning"
                variant="bordered"
                label="Warning"
                placeholder="Warning color"
                value={values['warning'] || ''}
                onValueChange={(value) => handleValueChange('warning', value)}
              />
              
              <Input
                color="danger"
                variant="bordered"
                label="Danger"
                placeholder="Danger color"
                value={values['danger'] || ''}
                onValueChange={(value) => handleValueChange('danger', value)}
              />
            </div>
          </CardBody>
        </Card>

        {/* States & Features */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <h2 className="text-xl font-semibold">States & Features</h2>
              <Badge variant="flat" size="sm">Interactive</Badge>
            </div>
          </CardHeader>
          <CardBody className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Input
                variant="bordered"
                label="Clearable Input"
                placeholder="Type to see clear button"
                value={values['clearable'] || ''}
                onValueChange={(value) => handleValueChange('clearable', value)}
                isClearable
                description="Clear button appears when typing"
              />
              
              <Input
                variant="bordered"
                label="Required Field"
                placeholder="This field is required"
                value={values['required'] || ''}
                onValueChange={(value) => handleValueChange('required', value)}
                isRequired
                color="primary"
              />
              
              <Input
                variant="bordered"
                label="Disabled Input"
                value={values['disabled'] || ''}
                onValueChange={(value) => handleValueChange('disabled', value)}
                isDisabled
                description="This input is disabled"
              />
              
              <Input
                variant="bordered"
                label="Read-only Input"
                value={values['readonly'] || ''}
                onValueChange={(value) => handleValueChange('readonly', value)}
                isReadOnly
                description="This input is read-only"
              />
            </div>
          </CardBody>
        </Card>

        {/* Validation */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <h2 className="text-xl font-semibold">Validation Example</h2>
              <Badge color="danger" variant="flat" size="sm">Real-time</Badge>
            </div>
          </CardHeader>
          <CardBody>
            <div className="max-w-md">
              <Input
                type="email"
                variant="bordered"
                label="Email Validation"
                placeholder="Enter your email"
                value={values['validated'] || ''}
                onValueChange={(value) => handleValueChange('validated', value)}
                onBlur={handleEmailValidation}
                isInvalid={!!errors['validated']}
                errorMessage={errors['validated']}
                color={errors['validated'] ? "danger" : "default"}
                startContent={<Mail className="w-4 h-4 text-default-400" />}
                description="Email will be validated on blur"
              />
            </div>
          </CardBody>
        </Card>

        {/* Label Placements */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <h2 className="text-xl font-semibold">Label Placements</h2>
              <Badge color="secondary" variant="flat" size="sm">4 positions</Badge>
            </div>
          </CardHeader>
          <CardBody className="space-y-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <Input
                variant="bordered"
                label="Inside Label"
                placeholder="Type here..."
                labelPlacement="inside"
                value={values['basic'] || ''}
                onValueChange={(value) => handleValueChange('basic', value)}
              />
              
              <Input
                variant="bordered"
                label="Outside Label"
                placeholder="Type here..."
                labelPlacement="outside"
                value={values['basic'] || ''}
                onValueChange={(value) => handleValueChange('basic', value)}
              />
              
              <Input
                variant="bordered"
                label="Outside Left"
                placeholder="Type here..."
                labelPlacement="outside-left"
                value={values['basic'] || ''}
                onValueChange={(value) => handleValueChange('basic', value)}
              />
              
              <Input
                variant="bordered"
                label="Outside Top"
                placeholder="Type here..."
                labelPlacement="outside-top"
                value={values['basic'] || ''}
                onValueChange={(value) => handleValueChange('basic', value)}
              />
            </div>
          </CardBody>
        </Card>

      </div>
    </PlaygroundPageLayout>
  );
};